import * as React from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  Textarea,
  toast,
} from "@eduviaos/ui-kit";
import { CalendarClock, Loader2, Trash2 } from "lucide-react";
import type { AdmissionEnquiry } from "@eduviaos/shared";
import { useSetFollowup } from "@/hooks/useAdmissions";

/**
 * FollowupDialog — Phase D3.
 *
 * Single-purpose dialog for setting / updating / clearing the next
 * follow-up on an enquiry. Drives off the same `PATCH /followup`
 * endpoint the BE auto-logs as a `followup_set` activity, so the IA
 * never has to "also remember" to log the change manually.
 *
 * Inputs:
 *   - `<input type="datetime-local">` — native picker so timezone
 *     handling matches the IA's locale. We convert to UTC ISO 8601
 *     before sending; the BE round-trips it back as UTC and the
 *     calendar/timeline components localise on render.
 *   - Optional note (≤500 chars enforced server-side; FE soft-warns
 *     at 480).
 *
 * Clearing: a tertiary "Clear follow-up" button sends `null` so the
 * card's chip disappears. Audit trail keeps a `followup_set` entry
 * with `metadata.cleared=true`.
 */

interface FollowupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enquiry: AdmissionEnquiry;
  onUpdated?: () => void;
}

const DEFAULT_HOUR = 10;
const DEFAULT_MIN = 0;

function isoToLocalInput(iso: string | null): string {
  if (!iso) {
    // Default placeholder = tomorrow at 10:00 local.
    const t = new Date();
    t.setDate(t.getDate() + 1);
    t.setHours(DEFAULT_HOUR, DEFAULT_MIN, 0, 0);
    return formatLocalForInput(t);
  }
  return formatLocalForInput(new Date(iso));
}

function formatLocalForInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIsoUtc(local: string): string {
  // `<input type="datetime-local">` serialises in the user's local
  // timezone with NO offset suffix. We construct a Date from it
  // (which interprets it as local) and call .toISOString() to get UTC.
  return new Date(local).toISOString();
}

export function FollowupDialog({ open, onOpenChange, enquiry, onUpdated }: FollowupDialogProps) {
  const mut = useSetFollowup(enquiry.id);
  const [localValue, setLocalValue] = React.useState(() =>
    isoToLocalInput(enquiry.next_followup_at),
  );
  const [note, setNote] = React.useState(enquiry.next_followup_note ?? "");
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setLocalValue(isoToLocalInput(enquiry.next_followup_at));
      setNote(enquiry.next_followup_note ?? "");
      setError(null);
    }
  }, [open, enquiry.id, enquiry.next_followup_at, enquiry.next_followup_note]);

  const isUpdate = enquiry.next_followup_at !== null;

  const onSave = async () => {
    setError(null);
    if (!localValue) {
      setError("Pick a date and time, or use Clear follow-up to remove the schedule.");
      return;
    }
    try {
      await mut.mutateAsync({
        next_followup_at: localInputToIsoUtc(localValue),
        next_followup_note: note.trim() || null,
      });
      toast.success(
        isUpdate
          ? `Updated follow-up for ${enquiry.student_name}.`
          : `Scheduled follow-up for ${enquiry.student_name}.`,
      );
      onUpdated?.();
      onOpenChange(false);
    } catch (e) {
      setError((e as { message?: string } | null)?.message ?? "Couldn't save follow-up.");
    }
  };

  const onClear = async () => {
    setError(null);
    try {
      await mut.mutateAsync({ next_followup_at: null, next_followup_note: null });
      toast.success(`Cleared follow-up for ${enquiry.student_name}.`);
      onUpdated?.();
      onOpenChange(false);
    } catch (e) {
      setError((e as { message?: string } | null)?.message ?? "Couldn't clear follow-up.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarClock className="size-4" aria-hidden="true" />
            {isUpdate ? "Update follow-up" : "Schedule follow-up"}
          </DialogTitle>
          <DialogDescription>
            {isUpdate
              ? `Reschedule when to next contact ${enquiry.student_name}'s family.`
              : `Set when to next reach out to ${enquiry.student_name}'s family.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="followup-when">When</Label>
            <Input
              id="followup-when"
              type="datetime-local"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              data-testid="followup-when-input"
            />
            <p className="text-muted-foreground text-[11px]">
              Saved in your local timezone, displayed across the platform in each viewer's locale.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="followup-note">Note (optional)</Label>
            <Textarea
              id="followup-note"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              placeholder="e.g., Confirm document submission"
              rows={3}
              data-testid="followup-note-input"
            />
            <p
              className={
                note.length >= 480
                  ? "text-[11px] text-amber-600 dark:text-amber-400"
                  : "text-muted-foreground text-[11px]"
              }
            >
              {note.length}/500 characters
            </p>
          </div>

          {error && (
            <p className="text-destructive text-xs" role="alert">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <div>
            {isUpdate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClear}
                disabled={mut.isPending}
                className="text-destructive hover:text-destructive"
                data-testid="followup-clear"
              >
                <Trash2 className="size-3.5" aria-hidden="true" />
                Clear follow-up
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <DialogClose asChild>
              <Button variant="outline" size="sm" disabled={mut.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button size="sm" onClick={onSave} disabled={mut.isPending} data-testid="followup-save">
              {mut.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <CalendarClock className="size-4" aria-hidden="true" />
              )}
              {isUpdate ? "Update" : "Schedule"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
